import { DimensionDetails, DimensionHierarchy, DimensionValue } from "../../interfaces/CoObject";
import {
  DimensionItem,
  FormTreeSelection,
  ScrollableFormTree as ScrollableFromTreeAmountSelector,
} from "./TreeAmountSelector";
import { Dropdown, mergeStyleSets, Modal, ResponsiveMode } from "@fluentui/react";
import React, { useEffect, useMemo, useState } from "react";
import LoadingManager from "../../../common/loading/LoadingManager";
import { FORM_TREE_NOTIFICATION } from "../../../utils/constants/notification";
import { MultiSelectPills } from "../../generic/MultiSelectPills";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import FooterPanel from "../../layout/FooterPanel";
import HeaderPanel from "../../layout/HeaderPanel";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import { formatMessage, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { SearchBar } from "../../generic/searchBar/SearchBar";
import { SearchQueryAndResults } from "./SearchQueryAndResults";

export const checkBeforeDelete = (
  dimensions: DimensionDetails[],
  valueToDelete: DimensionValue & { hierarchy: DimensionHierarchy }
) => {
  //this function starts with the assumption that dimensionsDetails passed as props are already filtered by dimensionOid
  return dimensions.reduce((acc, d) => {
    if (d.hierarchy?.oid !== valueToDelete.hierarchy?.oid) {
      return [...acc, d];
    }
    const values = d.values?.filter((v) => v.oid !== valueToDelete.oid);
    if (values?.length) {
      return [...acc, { ...d, values }];
    } else {
      return acc;
    }
  }, []);
};

interface TreeInputModalState {
  dimensions: (DimensionDetails | undefined)[];
  availableHierarchies?: DimensionItem[];
  currentHierarchy: DimensionItem | undefined;
  searchText: string;
}

function handleDimensionsChange(
  onlyOneValue: boolean,
  formSelection: FormTreeSelection,
  rootOid: string,
  oldDimensions: DimensionDetails[],
  currentHierarchy: DimensionHierarchy
) {
  const newDimensions = oldDimensions.filter(
    (d) => d.hierarchy?.oid !== currentHierarchy?.oid || d.dimensionOid !== rootOid
  );
  const oldChangedDimension = oldDimensions.find(
    (d) => d.hierarchy?.oid === currentHierarchy?.oid && d.dimensionOid === rootOid
  ) ?? {
    dimensionOid: rootOid,
    hierarchy: currentHierarchy,
    values: [],
    inherited: false,
  };
  const filteredValues = oldChangedDimension.values.filter((v) => v.oid !== formSelection.oid);

  if (formSelection.checked) {
    if (onlyOneValue) {
      newDimensions.push({
        ...oldChangedDimension,
        values: [
          {
            oid: formSelection.oid,
            oidPath: formSelection.oidPath,
            desc: formSelection.desc,
            code: formSelection.code,
          },
        ],
      });
      return newDimensions;
    }

    filteredValues.push({
      oid: formSelection.oid,
      oidPath: formSelection.oidPath,
      desc: formSelection.desc,
      code: formSelection.code,
    });
  }

  if (filteredValues.length)
    newDimensions.push({
      ...oldChangedDimension,
      values: filteredValues,
    });

  return newDimensions;
}

export const ModalSelectDimensionTree = ({
  isModalOpen,
  setModalOpen,
  onChange,
  dimensions,
  title,
  rootOid,
  hierarchical,
  onlyOneValue,
  fetchRoot,
  fetchChildren,
  dbId,
  isSearchAvailable,
}: {
  isModalOpen: boolean;
  setModalOpen: (x: boolean) => void;
  title: string;
  onChange: (dimensions: DimensionDetails[]) => void;
  rootOid: string;
  hierarchical: boolean;
  dimensions: (DimensionDetails | undefined)[];
  onlyOneValue?: boolean;
  fetchRoot: (parentOid: string) => Promise<DimensionItem[]>;
  fetchChildren: (parent: DimensionItem) => Promise<DimensionItem[]>;
  dbId?: string;
  isSearchAvailable: boolean;
}) => {
  const contentStyles = mergeStyleSets({
    body: {
      flex: "4 4 auto",
      padding: "1rem 1.5rem",
      marginBottom: "4rem",
      selectors: {
        p: { margin: "14px 0" },
        "p:first-child": { marginTop: 0 },
        "p:last-child": { marginBottom: 0 },
      },
    },
    multiSelect: {
      border: "1px solid var(--color-black)",
      marginTop: "0.25rem",
      padding: "0 0.25rem",
    },
    multiSelectHeaderContainer: {
      color: "var(--color-blue)",
      display: "flex",
      justifyContent: "space-between",
      marginTop: "0.75rem",
    },
    multiSelectSelectedCount: {
      fontSize: "0.9rem",
      marginTop: "auto",
      cursor: "pointer",
    },
  });

  const [state, setState] = useState<TreeInputModalState>({
    dimensions: dimensions,
    currentHierarchy: undefined,
    searchText: "",
  });

  const currentDimension = state.dimensions?.find((d) => d.hierarchy?.oid === state.currentHierarchy?.oid) ?? {
    dimensionOid: rootOid,
    values: [],
    hierarchy: state.currentHierarchy,
    inherited: false,
  };

  useEffect(() => {
    if (hierarchical) {
      LoadingManager.create(FORM_TREE_NOTIFICATION + "_AVAILABLE_HIERARCHY");
      fetchRoot(rootOid)
        .then((items) => {
          let hierarchyToUse = undefined;
          if (dimensions?.[0]?.hierarchy) hierarchyToUse = items.find((i) => i.oid === dimensions[0].hierarchy.oid);
          else if (items.length === 1) hierarchyToUse = items[0];
          setState((s) => ({
            ...s,
            availableHierarchies: items,
            currentHierarchy: hierarchyToUse,
          }));
        })
        .finally(() => LoadingManager.remove(FORM_TREE_NOTIFICATION + "_AVAILABLE_HIERARCHY"));
    }
    return () => LoadingManager.remove(FORM_TREE_NOTIFICATION + "_AVAILABLE_HIERARCHY");
  }, []);

  const onChangeFromTree = (formSelection: FormTreeSelection, overrideHierarchyOid?: string) => {
    setState((s) => {
      let hierarchyToUse = s.currentHierarchy;
      if (overrideHierarchyOid) hierarchyToUse = s.availableHierarchies?.find((h) => h.oid === overrideHierarchyOid);

      const newDimensions = handleDimensionsChange(onlyOneValue, formSelection, rootOid, s.dimensions, hierarchyToUse);
      return {
        ...s,
        dimensions: newDimensions,
      };
    });
  };

  const allValues = useMemo(() => {
    return (
      state.dimensions?.reduce((acc, d) => {
        return [...acc, ...d.values];
      }, []) ?? []
    );
  }, [state.dimensions]);

  let timer;
  function handleChangeSearch(event) {
    const newString = event.value;
    // eslint-disable-next-line no-undef
    clearTimeout(timer);
    // eslint-disable-next-line no-undef
    timer = setTimeout(() => {
      setState((s) => {
        return {
          ...s,
          searchText: newString,
        };
      });
    }, 1000);
  }

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={isModalOpen}
      onDismiss={() => setModalOpen(false)}
      isBlocking={false}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
        scrollableContent: {},
      }}
    >
      <HeaderPanel applyPadding>{title}</HeaderPanel>
      <div className={contentStyles.body}>
        {hierarchical ? (
          <>
            <Dropdown
              responsiveMode={ResponsiveMode.large}
              style={{ marginBottom: "10px" }}
              options={state.availableHierarchies?.map((h) => ({
                key: h.oid,
                text: h.code?.length ? h.code + " - " + h.desc : h.desc,
              }))}
              onChange={(_, newValue) => {
                setState((s) => {
                  const thisHierarchy = s.availableHierarchies.find((h) => h.oid === newValue.key.toString());

                  return {
                    ...s,
                    availableHierarchies: s.availableHierarchies,
                    currentHierarchy: thisHierarchy,
                  };
                });
              }}
              placeholder={formatMessage("cof.objectsource.placeholder.dimension.hierarchy")}
              selectedKey={state.currentHierarchy?.oid ?? null}
            />
          </>
        ) : null}
        {isSearchAvailable ? <SearchBar onChangeValue={handleChangeSearch}></SearchBar> : null}
        <ScrollableFromTreeAmountSelector
          hidden={state.searchText?.length >= 3}
          fetchRoot={fetchRoot}
          fetchChildren={fetchChildren}
          height={hierarchical ? "calc( 100vh - 13.5rem - 115px)" : "calc( 100vh - 13.5rem - 75px)"}
          rootKey={hierarchical ? state.currentHierarchy?.oid : rootOid}
          dimension={currentDimension}
          currentHierarchy={state.currentHierarchy}
          onChange={onChangeFromTree}
        />
        <SearchQueryAndResults
          searchText={state.searchText}
          onChangeFromTree={onChangeFromTree}
          currentDimensions={state.dimensions}
          currentHierarchy={state.currentHierarchy}
          rootOid={rootOid}
          dbId={dbId}
          hierarchical={hierarchical}
        />
        <div className={contentStyles.multiSelectHeaderContainer}>
          <span style={{ fontWeight: "bold" }}>
            {formatMessageWithParams("cof.objectsource.dimension.valuescount", {
              count: allValues.length ?? 0,
            })}
          </span>
          <span
            id={"clearAll"}
            className={contentStyles.multiSelectSelectedCount}
            style={{ color: allValues.length ? "var(--color-blue)" : "var(--color-disabled-text)" }}
            onClick={() => {
              setState((s) => ({
                ...s,
                dimensions: [],
              }));
            }}
          >
            {formatMessage("cof.objectsource.dimension.clearAll")}
          </span>
        </div>
        <MultiSelectPills
          placeholder={formatMessage("cof.objectsource.dimension.novalues")}
          onDelete={(valueToDelete) => {
            setState((s) => ({
              ...s,
              dimensions: checkBeforeDelete(s.dimensions, valueToDelete),
            }));
          }}
          onClick={() => setModalOpen(true)}
          className={contentStyles.multiSelect}
          values={state.dimensions}
        />
      </div>

      <FooterPanel>
        <PrimaryButton
          id={"confirm"}
          text={formatMessage("cof.base.confirm")}
          onClick={() => {
            onChange(state.dimensions);
            setModalOpen(false);
          }}
          disabled={allValues.length === 0}
          style={{ float: "right" }}
        />
        <PrimaryButton
          id={"cancel"}
          className={ButtonVariants.blue_outlined}
          text={formatMessage("cof.base.cancel")}
          onClick={() => {
            setModalOpen(false);
          }}
          style={{ float: "left" }}
        />
      </FooterPanel>
    </Modal>
  );
};
